import { useState } from "react";
import { Link } from "react-router-dom";
import { FaExternalLinkAlt } from "react-icons/fa";
import { RiEBike2Line, RiMapPinUserLine } from "react-icons/ri";
import { MdOutlineShoppingBag, MdOutlineRestaurant } from "react-icons/md";
import { useFetchRushOrdersListQuery, useTypedDispatch } from "../../store";
import Skeleton from "./Skeleton";
import {
  OrderType,
  RushOrder,
  RushOrderLabels,
  RushOrderType,
} from "../../types";
import Pagination from "./Pagination";
import RiderSelect from "./RiderSelect";
import OrderStatusSelect from "./OrderStatusSelect";
import classNames from "classnames";

interface RushOrdersListProps {
  rushOrderType: RushOrderType;
}

const RushOrdersList: React.FC<RushOrdersListProps> = ({ rushOrderType }) => {
  const dispatch = useTypedDispatch();
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);

  const { data, isFetching } = useFetchRushOrdersListQuery({
    page,
    perPage,
    rushType: rushOrderType,
  });

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-bold text-gray-800">
          {RushOrderLabels[rushOrderType]}
        </h2>
        <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-xs font-bold">
          {data?.paginationInfo?.count || 0}
        </span>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/80 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500 font-semibold">
                <th className="px-6 py-4 w-24">ID</th>
                <th className="px-6 py-4 w-16 text-center">Type</th>
                <th className="px-6 py-4 w-40">Statut</th>
                <th className="px-6 py-4">Restaurant</th>
                <th className="px-6 py-4">Référent</th>
                <th className="px-6 py-4 w-64">Livreur</th>
                <th className="px-6 py-4 text-center w-32">Création</th>
                <th className="px-6 py-4 w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isFetching ? (
                <tr>
                  <td
                    colSpan={8}
                    className="p-6"
                  >
                    <Skeleton
                      times={5}
                      className="h-12 w-full mb-2"
                    />
                  </td>
                </tr>
              ) : data?.data?.length ? (
                data.data.map((order: RushOrder) => (
                  <tr
                    key={order._id}
                    className="hover:bg-gray-50/50 transition-colors group"
                  >
                    <td className="px-6 py-4">
                      <span className="font-mono font-bold text-gray-700 bg-gray-100 px-2 py-1 rounded text-xs">
                        #{order.displayId}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        {order.orderType === OrderType.DELIVERY ? (
                          <div
                            title="Livraison"
                            className="bg-blue-50 text-blue-600 p-2 rounded-full"
                          >
                            <RiEBike2Line className="w-5 h-5" />
                          </div>
                        ) : (
                          <div
                            title="À emporter"
                            className="bg-orange-50 text-orange-600 p-2 rounded-full"
                          >
                            <MdOutlineShoppingBag className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <OrderStatusSelect
                        orderType={order.orderType}
                        orderId={order._id}
                        value={order.status}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="bg-orange-50 p-1.5 rounded-full text-orange-500">
                          <MdOutlineRestaurant className="w-4 h-4" />
                        </div>
                        <span className="font-medium text-gray-900 text-sm">
                          {order.restaurantInfo?.displayName}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-800">
                          {order.restaurantInfo?.referant?.fullName}
                        </span>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {order.restaurantInfo?.referant?.phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <RiderSelect
                          orderId={order._id}
                          value={order.rider || null}
                          placeholder="Assigner un livreur"
                        />
                        {order.rider?.phone && (
                          <div className="text-xs text-gray-400 pl-1">
                            📞 {order.rider.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center text-xs text-gray-500">
                      {order.createdAt}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={order._id}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-transparent hover:bg-primary/10 text-gray-400 hover:text-primary transition-all"
                        title="Voir les détails"
                      >
                        <FaExternalLinkAlt className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    className="text-center py-12 text-gray-500 italic"
                  >
                    Aucune commande urgente trouvée dans cette catégorie.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-gray-200 bg-gray-50 p-4">
          <Pagination
            paginationInfo={{
              page,
              perPage,
              count: data?.paginationInfo?.count || 1,
            }}
            setPage={setPage}
            setPerPage={setPerPage}
          />
        </div>
      </div>
    </div>
  );
};

export default RushOrdersList;
